/**
 * useTeamMatrixData  —  Phase 76.2.C
 *
 * Parallel data loader for the dynamic <PermissionsMatrix /> sub-component
 * mounted inside the Team Management edit dialog.
 *
 * It pulls three independent slices in parallel:
 *
 *   1. The full module catalog from `modules_v76`   (dual-locale labels)
 *   2. The role catalog       from `module_roles_v76`
 *   3. The active subscription set for the supplied company_id from
 *      `company_module_subscriptions_v76` — used to drive the grey-out guard
 *      on unsubscribed module columns.
 *
 * The hook is intentionally side-effect-free w.r.t. permissions: it does NOT
 * write to `company_member_module_roles_v76`. Persistence stays at the page
 * level (TeamSettingsPage.handleUpdateMember) for the cutover period — the
 * matrix only emits a controlled `value` / `onChange` pair upstream.
 *
 * Returns a strongly-typed view shape ready for the matrix grid.
 */

import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface MatrixModule {
  key:              string;
  display_name_he:  string;
  display_name_en:  string;
  icon:             string | null;
  sort_order:       number;
  /** True if the active company is subscribed to this module via
   *  company_module_subscriptions_v76. Drives the grey-out guard. */
  subscribed:       boolean;
}

export interface MatrixRole {
  /** Canonical role identifier from `module_roles_v76.role_key`
   *  (e.g. 'cashflow.viewer', 'hr.editor'). The grant table
   *  `company_member_module_roles_v76` stores a uuid FK to this role
   *  in its `role_id` column — resolve text↔uuid via this row's `id`. */
  role_key:         string;
  /** Stable surrogate key from `module_roles_v76.id` — useful for
   *  React keys and FK references that prefer uuids over text. */
  id:               string;
  module_key:       string | null;          // null => global role (rare)
  display_name_he:  string;
  display_name_en:  string;
  description:      string | null;
  sort_order:       number;
}

export interface MatrixData {
  modules:  MatrixModule[];
  roles:    MatrixRole[];
  loading:  boolean;
  error:    string | null;
  refresh:  () => Promise<void>;
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useTeamMatrixData(companyId: string | null): MatrixData {
  const [modules,  setModules]  = useState<MatrixModule[]>([]);
  const [roles,    setRoles]    = useState<MatrixRole[]>([]);
  const [loading,  setLoading]  = useState<boolean>(true);
  const [error,    setError]    = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!companyId) {
      setModules([]);
      setRoles([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Parallel fan-out: three independent SELECTs.
      const [catalogRes, rolesRes, subsRes] = await Promise.all([
        supabase
          .from('modules_v76')
          .select('key, display_name_he, display_name_en, icon, sort_order')
          .order('sort_order', { ascending: true }),
        // Phase 76.2.E hotfix: the canonical identifier columns on
        // `module_roles_v76` are `id` (uuid) + `role_key` (text) — NOT a
        // single `key` column. Selecting the non-existent `key` was raising
        // PostgREST 42703 and crashing the matrix mount inside the Team
        // edit dialog.
        supabase
          .from('module_roles_v76')
          .select('id, module_key, role_key, display_name_he, display_name_en, description, sort_order')
          .order('sort_order', { ascending: true }),
        supabase
          .from('company_module_subscriptions_v76')
          .select('module_key')
          .eq('company_id', companyId),
      ]);

      if (catalogRes.error) throw catalogRes.error;
      if (rolesRes.error)   throw rolesRes.error;
      if (subsRes.error)    throw subsRes.error;

      const subscribed = new Set<string>(
        (subsRes.data ?? []).map(r => r.module_key as string),
      );

      const mods: MatrixModule[] = (catalogRes.data ?? []).map(m => ({
        key:             m.key as string,
        display_name_he: (m.display_name_he as string) ?? (m.key as string),
        display_name_en: (m.display_name_en as string) ?? (m.key as string),
        icon:            (m.icon as string | null) ?? null,
        sort_order:      (m.sort_order as number) ?? 0,
        subscribed:      subscribed.has(m.key as string),
      }));

      const rls: MatrixRole[] = (rolesRes.data ?? []).map(r => {
        const roleKey = (r as { role_key: string }).role_key;
        return {
          id:              (r as { id: string }).id,
          role_key:        roleKey,
          module_key:      ((r as { module_key: string | null }).module_key) ?? null,
          display_name_he: ((r as { display_name_he: string | null }).display_name_he) ?? roleKey,
          display_name_en: ((r as { display_name_en: string | null }).display_name_en) ?? roleKey,
          description:     ((r as { description: string | null }).description) ?? null,
          sort_order:      ((r as { sort_order: number | null }).sort_order) ?? 0,
        };
      });

      setModules(mods);
      setRoles(rls);
    } catch (e) {
      const msg = e instanceof Error
        ? e.message
        : typeof e === 'object' && e !== null
          ? JSON.stringify(e)
          : String(e);
      console.error('[useTeamMatrixData] load failed:', e);
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { void load(); }, [load]);

  return { modules, roles, loading, error, refresh: load };
}
